import * as React from "react";

import { RouteComponentProps } from "react-router";
import { NumberParam, useQueryParams } from "use-query-params";

import useTranslations from "../hooks/useTranslations";

import useViewer from "../hooks/useViewer";

import UserDocumentsSection from "../components/users/UserDocumentsSection";
import UserFollowUpsSection from "../components/users/UserFollowUpsSection";
import UserHeader from "../components/users/UserHeader";
import UserNotesSection from "../components/users/UserNotesSection";
import UserSkillSection from "../components/users/UserSkillSection";
import UserGroups from "../components/users/UserGroups";

import {
  Container,
  EmptyState,
  Link,
  ScreenContainer,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from "../components/wombat-ui";
import { useBreadcrumbs } from "../hooks/useBreadcrumbs";
import { useGetUser } from '../graphql/generated/definitions';

type UserProfileScreenProps = RouteComponentProps<{ id: string }>;

const UserProfileScreen: React.FC<UserProfileScreenProps> = ({ match }) => {
  const t = useTranslations();

  const viewer = useViewer();
  const { data, loading, error } = useGetUser({
    variables: { id: match.params.id },
  });
  const user = data?.user;

  useBreadcrumbs([
    {
      to: "/users",
      text: t("users.header") as string,
    },
    {
      to: `/users/${match.params.id}`,
      text: user?.name,
    },
  ]);

  const [{ tab = 0 }, setQuery] = useQueryParams({
    tab: NumberParam,
  });
  const handleTabChange = React.useCallback(
    (tab: number) => setQuery({ tab }, "replaceIn"),
    []
  );

  return (
    <ScreenContainer
      loading={loading}
      error={
        error ? (
          <EmptyState
            title={t("network.unauthorized") as string}
            action={
              <Link to="/" variant="button">
                {t("network.backToHome")}
              </Link>
            }
          />
        ) : undefined
      }
    >
      {user && (
        <>
          <UserHeader user={user} />
          <Container>
            <div className="grid grid-cols-12 pb-6 gap-y-6 gap-x-10">
              {viewer.isCapableOfReadingUserNotes && (
                <div className="col-span-8">
                  <UserNotesSection user={user} />
                </div>
              )}
              <div
                className={
                  viewer.isCapableOfReadingUserNotes
                    ? "col-span-8 sm:col-span-4"
                    : "col-span-8"
                }
              >
                <UserGroups user={user} />
              </div>
            </div>
            <Tabs index={tab} onChange={handleTabChange}>
              <TabList>
                <Tab>{t("userProfile.followUps")}</Tab>
                <Tab>{t("userProfile.statistics")}</Tab>
                <Tab>{t("userProfile.documents")}</Tab>
              </TabList>
              <TabPanels>
                <TabPanel className="bg-white rounded-sm">
                  <UserFollowUpsSection user={user} />
                </TabPanel>
                <TabPanel className="bg-white rounded-sm">
                  <UserSkillSection user={user} />
                </TabPanel>
                <TabPanel className="bg-white rounded-sm">
                  <UserDocumentsSection user={user} vertical />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Container>
        </>
      )}
    </ScreenContainer>
  );
};

export default UserProfileScreen;
